import { rtLogService } from '@rammetec/core';
import { createWriteBatch, generateWriteBatchId, repositoryResolver } from '@rammetec/data-access';
import { INVOICE, INVOICE_STATUS_LABELS, } from '../types/invoice.js';
import { INVOICE_ITEM, } from '../types/invoice-item.js';
import { EntityValidationError } from '../validators/types.js';
import { validateInvoice } from '../validators/validateInvoice.js';
import { OrgScopedCrudService, VERSION_BYPASS, toVersion, } from './OrgScopedCrudService.js';
import { ChildCrudService } from './ChildCrudService.js';
/** Round to 2 decimals (money as number is fine for Phase 1). */
function round2(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}
function optNum(value) {
    if (value === undefined || value === null || value === '')
        return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}
function optStr(value) {
    if (value === undefined || value === null)
        return null;
    const s = String(value).trim();
    return s.length > 0 ? s : null;
}
function posNum(value) {
    const n = optNum(value);
    return n !== null && n > 0 ? n : null;
}
/** Allowed status transitions. Single source of truth, reusable by the UI. */
const ALLOWED_TRANSITIONS = {
    draft: ['authorized', 'cancelled'],
    authorized: ['cancelled'],
    cancelled: [],
};
/**
 * Whether `status` may move from `from` to `to`. A no-op transition (same status)
 * is always allowed (e.g. saving an edit without changing the status).
 */
export function canInvoiceTransition(from, to) {
    if (from === to)
        return true;
    return ALLOWED_TRANSITIONS[from]?.includes(to) ?? false;
}
const STATUS_LABEL = INVOICE_STATUS_LABELS;
const FISCAL_EXTRA_KEYS = [
    'discountAmount',
    'freightAmount',
    'insuranceAmount',
    'otherExpensesAmount',
    'icmsAmount',
    'icmsStAmount',
    'ipiAmount',
    'pisAmount',
    'cofinsAmount',
    'issAmount',
];
function hasFiscalExtras(header) {
    return FISCAL_EXTRA_KEYS.some((k) => {
        const n = optNum(header[k]);
        return n !== null && n > 0;
    });
}
/**
 * Domain service for org-scoped `Invoice` (org/{orgId}/invoice) and line items
 * (org/{orgId}/invoice/{invoiceId}/item).
 *
 * Singleton coordinator (mirrors SalesOrderService / PurchaseOrderService): owns
 * `(number, series, direction)` uniqueness, the status state machine, computed
 * line totals / productsAmount / itemCount, totalAmount rules, ≥1-item,
 * differential child reconciliation, and cascade delete. Header and children
 * are enqueued on a WriteBatch and committed once (atomic on PostgresRest via
 * `/pg/v1/_batch`; sequential elsewhere).
 */
class InvoiceService {
    constructor() {
        this.crud = new OrgScopedCrudService({
            collection: 'invoice',
            entity: INVOICE,
            versioned: true,
            audited: true,
        });
    }
    getItemRepo(orgId, invoiceId) {
        return repositoryResolver.resolveWithContext(INVOICE_ITEM, {
            orgId,
            invoiceId,
        });
    }
    async itemsCrud(orgId, invoiceId) {
        return new ChildCrudService(await this.getItemRepo(orgId, invoiceId), {
            entity: INVOICE_ITEM,
            audited: true,
        });
    }
    /** All invoices for an organization, sorted by issueDate desc then number. */
    async getInvoicesByOrganization(orgId) {
        try {
            const list = await this.crud.list(orgId);
            return [...list].sort((a, b) => {
                const da = a.issueDate ? new Date(a.issueDate).getTime() : 0;
                const db = b.issueDate ? new Date(b.issueDate).getTime() : 0;
                if (db !== da)
                    return db - da;
                return (a.number ?? '').localeCompare(b.number ?? '');
            });
        }
        catch (error) {
            rtLogService.error('Error fetching invoices by organization', { orgId, error });
            throw error;
        }
    }
    async getInvoiceItems(orgId, invoiceId) {
        try {
            const items = await this.itemsCrud(orgId, invoiceId);
            const rows = await items.getAll();
            return [...rows].sort((a, b) => (a.lineNumber ?? 0) - (b.lineNumber ?? 0));
        }
        catch (error) {
            rtLogService.error('Error fetching invoice items', { orgId, invoiceId, error });
            throw error;
        }
    }
    async createInvoice(orgId, header, items, opts) {
        try {
            const validationErrors = validateInvoice(header, items);
            if (validationErrors)
                throw new EntityValidationError(validationErrors);
            const number = (header.number ?? '').trim();
            const direction = header.direction;
            this._assertHeaderValid(number, direction, header.partyId);
            const status = header.status ?? 'draft';
            if (status !== 'draft') {
                throw new Error('Uma nova nota fiscal deve iniciar com o status Rascunho.');
            }
            const series = optStr(header.series);
            await this._assertBusinessKeyUnique(orgId, number, series, direction, undefined);
            const normalizedItems = this._normalizeItems(items);
            const productsAmount = this._sumTotal(normalizedItems);
            const totalAmount = this._resolveTotalAmount(header, productsAmount);
            const payload = {
                ...(opts?.id?.trim() ? { id: opts.id.trim() } : { id: generateWriteBatchId() }),
                ...this._headerFields(header, number, series, direction, status, productsAmount, totalAmount, normalizedItems.length),
            };
            const id = payload.id;
            const batch = createWriteBatch();
            const audit = await this.crud.beginAggregateAudit(orgId, {
                id,
                version: 1,
                record: payload,
            });
            const created = await this.crud.create(orgId, payload, undefined, {
                batch,
                aggregateAudit: audit,
            });
            const itemCrud = await this.itemsCrud(orgId, created.id);
            for (const item of normalizedItems) {
                await itemCrud.create({ ...item, invoiceId: created.id }, { batch, audit });
            }
            await this.crud.commitAggregateBatch(orgId, batch, audit);
            const invoice = await this.crud.getByIdFresh(orgId, created.id);
            if (!invoice)
                throw new Error('Nota fiscal não encontrada após criação.');
            rtLogService.info('Invoice created', {
                orgId,
                invoiceId: invoice.id,
                items: normalizedItems.length,
            });
            return invoice;
        }
        catch (error) {
            rtLogService.error('Error creating invoice', { orgId, error });
            throw error;
        }
    }
    async updateInvoice(orgId, invoiceId, header, items, version) {
        try {
            const validationErrors = validateInvoice(header, items);
            if (validationErrors)
                throw new EntityValidationError(validationErrors);
            const existing = await this.crud.getByIdFresh(orgId, invoiceId);
            if (!existing)
                throw new Error('Nota fiscal não encontrada.');
            // Early optimistic check — before any child write (request 0236 §6).
            this.crud.assertVersion(existing, version);
            const number = (header.number ?? '').trim();
            const direction = (header.direction ?? existing.direction);
            this._assertHeaderValid(number, direction, header.partyId ?? existing.partyId);
            const series = optStr(header.series);
            // Explicit early uniqueness — before any child write (§4).
            await this._assertBusinessKeyUnique(orgId, number, series, direction, invoiceId);
            const nextStatus = header.status ?? existing.status;
            if (!canInvoiceTransition(existing.status, nextStatus)) {
                throw new Error(`Transição de status inválida: ${STATUS_LABEL[existing.status]} → ${STATUS_LABEL[nextStatus]}.`);
            }
            const generatesStockEntry = header.generatesStockEntry !== undefined
                ? header.generatesStockEntry === true
                : existing.generatesStockEntry === true;
            if (existing.status === 'draft' &&
                nextStatus === 'authorized' &&
                direction === 'IN' &&
                generatesStockEntry) {
                throw new Error('Use Conciliar e dar entrada para autorizar esta nota com entrada de estoque.');
            }
            if (existing.status === 'authorized' &&
                nextStatus === 'cancelled' &&
                direction === 'IN' &&
                (existing.generatesStockEntry === true || generatesStockEntry)) {
                throw new Error('Use Cancelar (com estorno) para cancelar uma nota que gerou entrada de estoque.');
            }
            const normalizedItems = this._normalizeItems(items);
            const productsAmount = this._sumTotal(normalizedItems);
            const totalAmount = this._resolveTotalAmount(header, productsAmount);
            const itemCrud = await this.itemsCrud(orgId, invoiceId);
            const current = await itemCrud.getAll();
            const incomingIds = new Set(normalizedItems.map((i) => i.id));
            const payload = this._headerFields(header, number, series, direction, nextStatus, productsAmount, totalAmount, normalizedItems.length);
            const batch = createWriteBatch();
            const audit = await this.crud.beginAggregateAudit(orgId, {
                id: invoiceId,
                version: toVersion(existing.version) + 1,
                record: { ...existing, ...payload },
            });
            for (const item of normalizedItems) {
                const prev = current.find((c) => c.id === item.id);
                if (!prev) {
                    await itemCrud.create({ ...item, invoiceId }, { batch, audit });
                }
                else if (this._itemChanged(prev, item)) {
                    await itemCrud.update(item.id, { ...item, invoiceId }, { batch, audit, before: prev });
                }
            }
            for (const prev of current) {
                if (!incomingIds.has(prev.id)) {
                    await itemCrud.delete(prev.id, { batch, audit, before: prev });
                }
            }
            await this.crud.update(orgId, invoiceId, payload, undefined, {
                version,
                batch,
                aggregateAudit: audit,
            });
            await this.crud.commitAggregateBatch(orgId, batch, audit);
            const updated = await this.crud.getByIdFresh(orgId, invoiceId);
            if (!updated)
                throw new Error('Nota fiscal não encontrada após atualização.');
            rtLogService.info('Invoice updated', {
                orgId,
                invoiceId,
                items: normalizedItems.length,
            });
            return updated;
        }
        catch (error) {
            rtLogService.error('Error updating invoice', { orgId, invoiceId, error });
            throw error;
        }
    }
    /**
     * System write that increments version without comparing (receiving marks, stock authorize/cancel).
     */
    async patchFromSystem(orgId, id, patch, reason = 'invoice.patchFromSystem') {
        await this.crud.update(orgId, id, patch, undefined, {
            version: VERSION_BYPASS,
            audit: { source: 'system', reason },
        });
    }
    /** Cascade delete — children first so a failure can't orphan items under a deleted parent. */
    async deleteInvoice(orgId, invoiceId) {
        try {
            const existing = await this.crud.getByIdFresh(orgId, invoiceId);
            if (!existing)
                throw new Error('Nota fiscal não encontrada.');
            const itemCrud = await this.itemsCrud(orgId, invoiceId);
            const items = await itemCrud.getAll();
            const batch = createWriteBatch();
            const audit = await this.crud.beginAggregateAudit(orgId, {
                id: invoiceId,
                version: null,
                record: existing,
            });
            for (const item of items) {
                await itemCrud.delete(item.id, { batch, audit, before: item });
            }
            await this.crud.delete(orgId, invoiceId, { batch, aggregateAudit: audit });
            await this.crud.commitAggregateBatch(orgId, batch, audit);
            rtLogService.info('Invoice deleted', { orgId, invoiceId, items: items.length });
        }
        catch (error) {
            rtLogService.error('Error deleting invoice', { orgId, invoiceId, error });
            throw error;
        }
    }
    _assertHeaderValid(number, direction, partyId) {
        if (!number)
            throw new Error('O número da nota fiscal é obrigatório.');
        if (direction !== 'IN' && direction !== 'OUT') {
            throw new Error('A direção da nota fiscal é obrigatória (entrada ou saída).');
        }
        if (!partyId)
            throw new Error('A parte (cliente/fornecedor) é obrigatória.');
    }
    _resolveTotalAmount(header, productsAmount) {
        const provided = optNum(header.totalAmount);
        const extras = hasFiscalExtras(header);
        let totalAmount;
        if ((provided === null || provided === 0) && !extras) {
            totalAmount = productsAmount;
        }
        else {
            totalAmount = provided ?? 0;
        }
        if (totalAmount < 0)
            throw new Error('O valor total não pode ser negativo.');
        return round2(totalAmount);
    }
    _headerFields(header, number, series, direction, status, productsAmount, totalAmount, itemCount) {
        const issuerKind = optStr(header.issuerKind);
        return {
            number,
            series,
            accessKey: optStr(header.accessKey),
            protocol: optStr(header.protocol),
            legacyType: optNum(header.legacyType),
            direction,
            issuerKind: issuerKind === 'OWN' || issuerKind === 'THIRD_PARTY' ? issuerKind : null,
            partyId: header.partyId,
            issueDate: header.issueDate ?? null,
            entryDate: header.entryDate ?? null,
            cfop: optStr(header.cfop),
            cfopComplement: optStr(header.cfopComplement),
            state: optStr(header.state),
            freightType: optStr(header.freightType),
            paymentTerm: optStr(header.paymentTerm),
            paymentMethod: optStr(header.paymentMethod),
            carrierId: optStr(header.carrierId),
            salespersonId: optStr(header.salespersonId),
            customerOrderRef: optStr(header.customerOrderRef),
            notes: optStr(header.notes),
            productsAmount,
            discountAmount: optNum(header.discountAmount),
            freightAmount: optNum(header.freightAmount),
            insuranceAmount: optNum(header.insuranceAmount),
            otherExpensesAmount: optNum(header.otherExpensesAmount),
            icmsAmount: optNum(header.icmsAmount),
            icmsStAmount: optNum(header.icmsStAmount),
            ipiAmount: optNum(header.ipiAmount),
            pisAmount: optNum(header.pisAmount),
            cofinsAmount: optNum(header.cofinsAmount),
            issAmount: optNum(header.issAmount),
            totalAmount,
            itemCount,
            status,
            ...(header.generatesStockEntry !== undefined
                ? { generatesStockEntry: header.generatesStockEntry === true }
                : {}),
            netWeight: optNum(header.netWeight),
            grossWeight: optNum(header.grossWeight),
            volumeCount: optNum(header.volumeCount),
        };
    }
    _normalizeItems(items) {
        const valid = (items ?? []).filter((i) => i.productId);
        if (valid.length === 0)
            throw new Error('Uma nota fiscal deve ter ao menos um item.');
        return valid.map((i, index) => {
            const quantity = Number(i.quantity) || 0;
            const unitPrice = Number(i.unitPrice) || 0;
            const baseQuantity = posNum(i.baseQuantity);
            const conversionFactor = posNum(i.conversionFactor);
            return {
                id: i.id && i.id.trim() ? i.id : crypto.randomUUID(),
                invoiceId: '',
                lineNumber: index + 1,
                productId: i.productId,
                variantId: i.variantId ?? null,
                description: (i.description ?? '').trim(),
                unit: optStr(i.unit),
                quantity,
                unitPrice,
                baseQuantity,
                baseUnit: optStr(i.baseUnit),
                conversionFactor,
                totalPrice: round2(quantity * unitPrice),
                discountAmount: optNum(i.discountAmount),
                cfop: optStr(i.cfop),
                icmsRate: optNum(i.icmsRate),
                icmsAmount: optNum(i.icmsAmount),
                icmsStAmount: optNum(i.icmsStAmount),
                ipiRate: optNum(i.ipiRate),
                ipiAmount: optNum(i.ipiAmount),
                pisAmount: optNum(i.pisAmount),
                cofinsAmount: optNum(i.cofinsAmount),
                costAmount: optNum(i.costAmount),
                warehouseId: optStr(i.warehouseId),
                purchaseOrderRef: optStr(i.purchaseOrderRef),
                purchaseOrderItemRef: optStr(i.purchaseOrderItemRef),
                lot: optStr(i.lot),
                expiryDate: i.expiryDate ?? null,
                width: optNum(i.width),
                height: optNum(i.height),
            };
        });
    }
    _sumTotal(items) {
        return round2(items.reduce((sum, i) => sum + i.totalPrice, 0));
    }
    _itemChanged(prev, next) {
        return (prev.lineNumber !== next.lineNumber ||
            prev.productId !== next.productId ||
            (prev.variantId ?? null) !== (next.variantId ?? null) ||
            prev.description !== next.description ||
            prev.unit !== next.unit ||
            prev.quantity !== next.quantity ||
            prev.unitPrice !== next.unitPrice ||
            prev.baseQuantity !== next.baseQuantity ||
            prev.baseUnit !== next.baseUnit ||
            prev.conversionFactor !== next.conversionFactor ||
            prev.totalPrice !== next.totalPrice ||
            prev.discountAmount !== next.discountAmount ||
            prev.cfop !== next.cfop ||
            prev.icmsRate !== next.icmsRate ||
            prev.icmsAmount !== next.icmsAmount ||
            prev.icmsStAmount !== next.icmsStAmount ||
            prev.ipiRate !== next.ipiRate ||
            prev.ipiAmount !== next.ipiAmount ||
            prev.pisAmount !== next.pisAmount ||
            prev.cofinsAmount !== next.cofinsAmount ||
            prev.costAmount !== next.costAmount ||
            prev.warehouseId !== next.warehouseId ||
            prev.purchaseOrderRef !== next.purchaseOrderRef ||
            prev.purchaseOrderItemRef !== next.purchaseOrderItemRef ||
            prev.lot !== next.lot ||
            String(prev.expiryDate ?? '') !== String(next.expiryDate ?? '') ||
            prev.width !== next.width ||
            prev.height !== next.height);
    }
    async _assertBusinessKeyUnique(orgId, number, series, direction, currentId) {
        const seriesKey = series ?? '';
        const all = await this.crud.list(orgId);
        const conflict = all.find((o) => o.number?.trim() === number &&
            (o.series ?? '').trim() === seriesKey &&
            o.direction === direction &&
            o.id !== currentId);
        if (conflict) {
            throw new Error('Já existe uma nota fiscal com o mesmo número, série e direção nesta organização.');
        }
    }
}
export const invoiceService = new InvoiceService();
//# sourceMappingURL=invoiceService.js.map